import { useTranslation } from "react-i18next";
import type {
  CreativeMaterialRole,
  CreativeUsage,
} from "@digital-twins/shared-types";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Text } from "@/components/ui/Text";
import { CreativeEditorSection } from "./CreativeEditorDialog";
import type { CreativeReferenceOptionsProps } from "@/types/creative-library";

export function CreativeReferenceOptions({
  role,
  usage,
  hideRole,
  roleDisabled,
  disabled,
  onRoleChange,
  onUsageChange,
}: CreativeReferenceOptionsProps) {
  const { t } = useTranslation("app");
  return (
    <CreativeEditorSection>
      <div className="grid gap-4 sm:grid-cols-2">
        {!hideRole && (
          <div className="flex flex-col gap-2">
            <Label>{t("creativeLibrary.referenceRole")}</Label>
            <Select
              value={role}
              disabled={roleDisabled}
              onValueChange={(value) =>
                onRoleChange(value as CreativeMaterialRole)
              }
            >
              <SelectTrigger
                aria-label={t("creativeLibrary.referenceRole")}
                data-testid="creative-upload-role"
              >
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {(
                  [
                    "product",
                    "logo",
                    "style",
                    "composition",
                    "identity",
                  ] as const
                ).map((value) => (
                  <SelectItem key={value} value={value}>
                    {t(`creativeLibrary.roles.${value}`)}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        )}
        <div className="flex flex-col gap-2">
          <Label>{t("creativeLibrary.allowedUsage")}</Label>
          <Select
            value={usage}
            disabled={disabled}
            onValueChange={(value) => onUsageChange(value as CreativeUsage)}
          >
            <SelectTrigger
              aria-label={t("creativeLibrary.allowedUsage")}
              data-testid="creative-upload-usage"
            >
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              {(
                ["unspecified", "internal_only", "external_allowed"] as const
              ).map((value) => (
                <SelectItem key={value} value={value}>
                  {t(`creativeLibrary.usage.${value}`)}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>
      <Text size="xs" color="muted">
        {t("creativeLibrary.allowedUsageHint")}
      </Text>
    </CreativeEditorSection>
  );
}
